import * as vp from 'vopay';
import { InteracWordmark } from 'vopay';
import { FileUploadForm, FileUploadProduct } from '../types';
import { useTranslation } from 'react-i18next';
import { FieldData } from '@/components/Form/types';
import * as Yup from 'yup';

export const useFileUploadDetailsFields = (
  Country: string,
  ClientAccountsEnabled: boolean,
): FieldData<FileUploadForm>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.Form' });

  const clientAccountProductOptions: { value: FileUploadProduct; label: string | React.ReactNode }[] = [
    { value: 'Client Account Transfer', label: t('ProductOptions.clientAccountTransfer') },
  ];

  const serviceOptions = [
    { value: 'Debit', label: t('ProductOptions.debitCollectFunds') },
    { value: 'Credit', label: t('ProductOptions.creditSendFunds') },
  ];

  const basicFields: FieldData<FileUploadForm>[] = [
    {
      fieldName: 'Service' as keyof FileUploadForm,
      label: () => t('serviceLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        return (
          <vp.Select
            version="2"
            {...getFormikData<FileUploadForm['Service']>({})}
            options={serviceOptions}
            placeholder=""
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'Product' as keyof FileUploadForm,
      label: () => t('productLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        let options: Array<{ value: FileUploadProduct; label: string | React.ReactNode }> = [];

        // Filter products based on selected service
        if (ctx.values.Service === 'Credit') {
          // Credit (Send Funds) services
          if (Country === 'US') {
            options = [
              { value: 'ACH', label: 'ACH' },
              { value: 'ePay', label: 'eLinx™ Pay' },
              { value: 'OCT', label: 'OCT' },
              { value: 'RTP', label: 'RTP' },
              { value: 'Wire', label: 'Wire' },
              ...(ClientAccountsEnabled ? clientAccountProductOptions : []),
            ];
          } else {
            options = [
              { value: 'EFT', label: 'EFT' },
              { value: 'VopayInstant', label: 'VopayInstant' },
              { value: 'e-Transfer', label: <InteracWordmark /> },
              { value: 'ePay', label: 'eLinx™ Pay' },
              { value: 'OCT', label: 'OCT' },
              ...(ClientAccountsEnabled ? clientAccountProductOptions : []),
            ];
          }
        } else if (ctx.values.Service === 'Debit') {
          // Debit (Collect Funds) services
          if (Country === 'US') {
            options = [
              { value: 'ACH', label: 'ACH' },
              { value: 'Credit Card', label: t('ProductOptions.creditCard') },
              { value: 'eCollect', label: 'eLinx™ Collect' },
              { value: 'eFlex', label: 'eLinx™ Flexible Payments' },
            ];
          } else {
            options = [
              { value: 'EFT', label: 'EFT' },
              { value: 'VopayInstant', label: 'VopayInstant' },
              { value: 'e-Transfer', label: <InteracWordmark /> },
              { value: 'Credit Card', label: t('ProductOptions.creditCard') },
              { value: 'eCollect', label: 'eLinx™ Collect' },
              { value: 'eFlex', label: 'eLinx™ Flexible Payments' },
            ];
          }
        }

        return (
          <vp.Select
            version="2"
            {...getFormikData<FileUploadForm['Product']>({})}
            options={options}
            placeholder=""
            sort={false}
          />
        );
      },
    },
    {
      fieldName: 'Currency' as keyof FileUploadForm,
      label: () => t('currencyLabel'),
      getInputState: () => 'required',
      showField: (values) => !['ePay', 'eCollect', 'eFlex'].includes(values.Product),
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        let currencyOptions = Object.entries(vp.CURRENCY_LIST_OPTIONS).map(([value, label]) => ({
          value,
          label,
        }));

        if (ctx.values.Product === 'ACH' || ctx.values.Product === 'RTP' || ctx.values.Product === 'Wire') {
          currencyOptions = Object.entries(vp.US_CURRENCY_ONLY).map(([value, label]) => ({
            value,
            label,
          }));
        } else if (ctx.values.Product === 'OCT' || ctx.values.Product === 'Credit Card') {
          currencyOptions = Object.entries(vp.CURRENCY_LIST_OPTIONS).map(([value, label]) => ({
            value,
            label,
          }));
        } else {
          const sourceOptions = ctx.values.Product !== 'EFT' ? vp.CA_CURRENCY_ONLY : vp.CURRENCY_LIST_OPTIONS;

          currencyOptions = Object.entries(sourceOptions).map(([value, label]) => ({
            value,
            label,
          }));
        }

        return (
          <vp.Select
            version="2"
            {...getFormikData<FileUploadForm['Currency']>({})}
            options={currencyOptions}
            placeholder=""
            sort={false}
          />
        );
      },
    },
  ];

  return basicFields;
};

export function useFileUploadFields(): FieldData<FileUploadForm>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.Form' });

  return [
    {
      fieldName: 'FileBase64' as keyof FileUploadForm,
      label: () => t('fileNameLabel'),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        return (
          <vp.UploadFile
            id="FileBase64"
            {...getFormikData<FileUploadForm['FileBase64']>({})}
            variant="onboarding"
            onBlurFileName={(e) => ctx.handleBlur(e)}
            onChangeFileName={(e) => ctx.setFieldValue('FileName', e.target.value)}
            idFileName="FileName"
            valueFileName={ctx.values.FileName}
            maxMBSize={20}
            validExtensions={['csv', 'txt', 'xlsx', 'xls', 'odf', 'odf', 'tsv', 'deposits_out']}
            accept=".csv,.txt,.xlsx,.xls,.odf,.odf,.tsv,.deposits_out"
            keepExtension={true}
          />
        );
      },
    },
  ];
}
